import { useMemo, useState } from "react";
import { api } from "../api/client";
import type { ErrorEventRow, ErrorGroupRow } from "../api/types";
import { PageHeader } from "../components/PageHeader";
import { EmptyView, ErrorView, LoadingView } from "../components/StateView";
import { absoluteTime, formatNumber, relativeTime, shortDeviceId } from "../format";
import { useApi } from "../useApi";
import styles from "./ErrorsPage.module.css";

type View = "groups" | "recent";

/**
 * Auto-captured error feed. Two views:
 *   - Groups (default): identical errors rolled up by fingerprint.
 *     Lets the dev see "this error fired 1,247 times across 89
 *     devices" without scrolling through every row.
 *   - Recent: chronological feed of individual rows. Useful for
 *     drilling into a specific incident.
 */
export function ErrorsPage() {
  const [view, setView] = useState<View>("groups");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const state = useApi(
    () => api.errors({ view, limit: view === "groups" ? 200 : 500 }),
    [view],
  );

  return (
    <>
      <PageHeader
        title="Errors"
        subtitle="Crashes + recovery events the suite captured automatically. Opt-in only."
        right={
          <div className={styles.viewToggle} role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={view === "groups"}
              className={
                view === "groups"
                  ? `${styles.viewToggleBtn} ${styles.viewToggleBtnActive}`
                  : styles.viewToggleBtn
              }
              onClick={() => {
                setView("groups");
                setSelectedId(null);
              }}
            >
              Grouped
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === "recent"}
              className={
                view === "recent"
                  ? `${styles.viewToggleBtn} ${styles.viewToggleBtnActive}`
                  : styles.viewToggleBtn
              }
              onClick={() => {
                setView("recent");
                setSelectedId(null);
              }}
            >
              Recent
            </button>
          </div>
        }
      />

      {state.kind === "loading" ? <LoadingView /> : null}
      {state.kind === "error" ? (
        <ErrorView message={state.message} onRetry={state.reload} />
      ) : null}
      {state.kind === "ready" ? (
        view === "groups" ? (
          <GroupsView
            rows={state.data.rows as ErrorGroupRow[]}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        ) : (
          <RecentView
            rows={state.data.rows as ErrorEventRow[]}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        )
      ) : null}
    </>
  );
}

/* ============================================================== */

function GroupsView({
  rows,
  selectedId,
  onSelect,
}: {
  rows: ErrorGroupRow[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  if (rows.length === 0) {
    return <EmptyView label="No errors yet — nothing's broken or no one's opted in." />;
  }
  const selected =
    selectedId !== null
      ? rows.find((r) => r.fingerprint === selectedId) ?? null
      : null;
  return (
    <div className={styles.layout}>
      <ul className={styles.list}>
        {rows.map((r) => (
          <li key={r.fingerprint}>
            <button
              type="button"
              onClick={() => onSelect(r.fingerprint)}
              className={
                r.fingerprint === selectedId
                  ? `${styles.row} ${styles.rowActive}`
                  : styles.row
              }
            >
              <span className={styles.rowTitleRow}>
                <code className={styles.rowKind}>{r.kind}</code>
                <span className={styles.rowOccurrences}>
                  {formatNumber(r.occurrences)}×
                </span>
              </span>
              <span className={styles.rowMessage}>{r.message}</span>
              <span className={styles.rowMeta}>
                {r.app_id ?? "—"}
                {" · "}
                {formatNumber(r.devices)} device{r.devices === 1 ? "" : "s"}
                {" · "}
                last <span title={absoluteTime(r.last_seen)}>{relativeTime(r.last_seen)}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
      <section className={styles.detail}>
        {selected ? (
          <GroupDetail group={selected} />
        ) : (
          <div className={styles.detailPlaceholder}>
            Pick a group to see details + occurrence stats.
          </div>
        )}
      </section>
    </div>
  );
}

function GroupDetail({ group }: { group: ErrorGroupRow }) {
  return (
    <article>
      <code className={styles.detailKind}>{group.kind}</code>
      <h2 className={styles.detailMessage}>{group.message}</h2>
      <dl className={styles.metaList}>
        <Meta label="Fingerprint" value={group.fingerprint} mono />
        <Meta label="App" value={group.app_id ?? "—"} mono />
        <Meta label="App version" value={group.app_version ?? "—"} mono />
        <Meta
          label="Occurrences"
          value={formatNumber(group.occurrences)}
          mono
        />
        <Meta label="Unique devices" value={formatNumber(group.devices)} mono />
        <Meta
          label="First seen"
          value={absoluteTime(group.first_seen)}
        />
        <Meta
          label="Last seen"
          value={absoluteTime(group.last_seen)}
        />
      </dl>
      <p className={styles.detailHint}>
        Switch to <strong>Recent</strong> view and filter by this fingerprint
        to see the individual rows for this group, with stack traces.
      </p>
    </article>
  );
}

/* ============================================================== */

function RecentView({
  rows,
  selectedId,
  onSelect,
}: {
  rows: ErrorEventRow[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  if (rows.length === 0) {
    return <EmptyView label="No errors yet." />;
  }
  const selected =
    selectedId !== null ? rows.find((r) => r.id === selectedId) ?? null : null;
  return (
    <div className={styles.layout}>
      <ul className={styles.list}>
        {rows.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => onSelect(r.id)}
              className={
                r.id === selectedId
                  ? `${styles.row} ${styles.rowActive}`
                  : styles.row
              }
            >
              <span className={styles.rowTitleRow}>
                <code className={styles.rowKind}>{r.kind}</code>
                {r.count > 1 ? (
                  <span className={styles.rowOccurrences}>{r.count}×</span>
                ) : null}
              </span>
              <span className={styles.rowMessage}>{r.message}</span>
              <span className={styles.rowMeta}>
                {r.app_id ?? "—"}
                {r.suite_version ? ` · v${r.suite_version}` : null}
                {" · "}
                <span title={absoluteTime(r.created_at)}>
                  {relativeTime(r.created_at)}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
      <section className={styles.detail}>
        {selected ? (
          <RowDetail row={selected} />
        ) : (
          <div className={styles.detailPlaceholder}>
            Pick a row to see stack + payload.
          </div>
        )}
      </section>
    </div>
  );
}

function RowDetail({ row }: { row: ErrorEventRow }) {
  const payload = useMemo(() => {
    if (!row.payload) return null;
    try {
      return JSON.stringify(JSON.parse(row.payload), null, 2);
    } catch {
      return row.payload;
    }
  }, [row.payload]);

  return (
    <article>
      <code className={styles.detailKind}>{row.kind}</code>
      <h2 className={styles.detailMessage}>{row.message}</h2>
      <dl className={styles.metaList}>
        <Meta label="When" value={absoluteTime(row.created_at)} />
        <Meta label="App" value={row.app_id ?? "—"} mono />
        <Meta label="App version" value={row.app_version ?? "—"} mono />
        <Meta label="Suite" value={row.suite_version ?? "—"} mono />
        <Meta label="OS" value={`${row.os ?? "—"} ${row.arch ?? ""}`.trim()} />
        <Meta label="Device" value={shortDeviceId(row.device_id)} mono />
        <Meta label="Fingerprint" value={row.fingerprint} mono />
        {row.count > 1 ? (
          <Meta
            label="Occurrences in batch"
            value={formatNumber(row.count)}
            mono
          />
        ) : null}
      </dl>

      {row.stack ? (
        <>
          <h3 className={styles.sectionHeading}>Stack</h3>
          <pre className={styles.code}>{row.stack}</pre>
        </>
      ) : null}

      {payload ? (
        <>
          <h3 className={styles.sectionHeading}>Payload</h3>
          <pre className={styles.code}>{payload}</pre>
        </>
      ) : null}
    </article>
  );
}

/* ============================================================== */

function Meta({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className={styles.metaRow}>
      <dt className={styles.metaLabel}>{label}</dt>
      <dd className={mono ? styles.metaValueMono : styles.metaValue}>{value}</dd>
    </div>
  );
}
